import React from 'react';
import {
  PiFileTextLight,
  PiUserPlusLight,
  PiUserMinusLight,
  PiArrowsLeftRightLight,
  PiFlagLight,
  PiCalendarBlankLight,
  PiCheckCircleLight,
  PiTargetLight,
  PiWarningLight,
  PiNoteLight,
} from 'react-icons/pi';
import { formatEventType } from '../../../utils/functions';

/**
 * How an event_type reads: its glyph and its badge colour. Kept apart from
 * FeedEventItem so the activity/audit surfaces can reuse the same vocabulary.
 */

type BadgeColor = 'blue' | 'green' | 'red' | 'purple' | 'orange' | 'yellow' | 'gray';

export const feedEventGlyph = (eventType: string): React.ReactNode => {
  switch (eventType) {
    case 'team_created':
    case 'department_created':
    case 'task_created':
      return <PiFileTextLight />;
    case 'member_added':
    case 'collaborator_added':
      return <PiUserPlusLight />;
    case 'member_removed':
    case 'collaborator_removed':
      return <PiUserMinusLight />;
    case 'status_changed':
      return <PiArrowsLeftRightLight />;
    case 'priority_changed':
      return <PiFlagLight />;
    case 'due_date_changed':
      return <PiCalendarBlankLight />;
    case 'description_updated':
    case 'update':
      return <PiCheckCircleLight />;
    case 'milestone':
      return <PiTargetLight />;
    case 'issue':
      return <PiWarningLight />;
    case 'note':
      return <PiNoteLight />;
    case 'action':
      return <PiCheckCircleLight />;
    default:
      return <PiFileTextLight />;
  }
};

export const feedEventBadgeColor = (eventType: string): BadgeColor => {
  switch (eventType) {
    case 'team_created':
    case 'department_created':
    case 'task_created':
    case 'milestone':
      return 'blue';
    case 'member_added':
    case 'collaborator_added':
    case 'action':
      return 'green';
    case 'member_removed':
    case 'collaborator_removed':
      return 'red';
    case 'description_updated':
    case 'update':
    case 'status_changed':
      return 'purple';
    case 'priority_changed':
    case 'issue':
      return 'orange';
    case 'due_date_changed':
      return 'yellow';
    default:
      return 'gray';
  }
};

/**
 * The rail glyph's disc tint, keyed to the same colour the badge uses, so the
 * icon on the timeline is colour-separated by type instead of a uniform grey
 * (UI feedback P-5). Uses the app's semantic scales (info/success/danger/
 * warning/purple) that `Badge` already draws from; `gray` keeps the neutral
 * surface disc.
 */
const DISC_TONE: Record<BadgeColor, string> = {
  blue: 'bg-info-50 dark:bg-info-900/30 border-info-200 dark:border-info-800 text-info-600 dark:text-info-300',
  green: 'bg-success-50 dark:bg-success-900/30 border-success-200 dark:border-success-800 text-success-600 dark:text-success-300',
  red: 'bg-danger-50 dark:bg-danger-900/30 border-danger-200 dark:border-danger-800 text-danger-600 dark:text-danger-300',
  purple: 'bg-purple-50 dark:bg-purple-900/30 border-purple-200 dark:border-purple-800 text-purple-600 dark:text-purple-300',
  orange: 'bg-warning-50 dark:bg-warning-900/30 border-warning-200 dark:border-warning-800 text-warning-700 dark:text-warning-300',
  yellow: 'bg-warning-50 dark:bg-warning-900/30 border-warning-200 dark:border-warning-800 text-warning-600 dark:text-warning-300',
  gray: 'bg-surface border-hairline text-fg-muted',
};

export const feedEventDiscClass = (eventType: string): string =>
  DISC_TONE[feedEventBadgeColor(eventType)];

export const feedEventLabel = (eventType: string): string => formatEventType(eventType);
